import { QueryKeys, useGetCheckoutStatus } from '@sunrise-ui/api-client';
import { useCheckoutModalStore } from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useSubmitQuestionnaire } from './useSubmitQuestionnaire';

type UseCheckoutCompletionParams = {
  checkoutId: string | undefined;
  onComplete: () => void;
  onError: (error: string) => void;
};

const { ticketsKey, byEventIdKey, temporalKey } = QueryKeys.tickets;
const CHECKOUT_TIMEOUT_MS = 30000; // 30 seconds

/**
 * Hook to poll checkout status and handle completion/failure.
 * Includes a 30-second timeout to prevent indefinite polling.
 */
export const useCheckoutCompletion = ({
  checkoutId,
  onComplete,
  onError,
}: UseCheckoutCompletionParams) => {
  const { eventId } = useCheckoutModalStore();
  const { data: checkoutStatus } = useGetCheckoutStatus({
    checkoutId,
  });
  const { submitQuestionnaire } = useSubmitQuestionnaire();
  const queryClient = useQueryClient();
  const hasCompletedRef = useRef(false);

  const handleComplete = () => {
    onComplete();
    submitQuestionnaire();
    void queryClient.invalidateQueries({
      queryKey: [ticketsKey, temporalKey],
    });
    void queryClient.invalidateQueries({
      queryKey: [ticketsKey, byEventIdKey, eventId],
    });
  };

  // For free tickets (no checkoutId), complete immediately
  useEffect(() => {
    if (!checkoutId && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      handleComplete();
    }
  }, [checkoutId, onComplete, queryClient, eventId]);

  // Set up timeout when component mounts
  useEffect(() => {
    if (!checkoutId) return;

    const timeoutId = setTimeout(() => {
      if (!hasCompletedRef.current) {
        onError('Please check your email or contact support.');
      }
    }, CHECKOUT_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [checkoutId, onError]);

  useEffect(() => {
    if (hasCompletedRef.current) return;

    const status = checkoutStatus?.status;
    if (status === 'completed') {
      hasCompletedRef.current = true;
      handleComplete();
    } else if (status === 'failed') {
      hasCompletedRef.current = true;
      onError(checkoutStatus?.error_message || 'An error occurred');
    }
  }, [checkoutStatus, onComplete, onError, queryClient, eventId]);
};
